import { queryKey } from '../constants/queryClientKey';
import {
  changeOrderDevice,
  createOrderGroup,
  deleteOrder,
  getDeviceStatistics,
  getDeviceStatisticsAll,
  getOrderDetail,
  getOrders,
  getOrderWorklist,
  matchOrder,
  ParamsChangeOrderDevice,
  ParamsCreateOrderGroup,
  ParamsDeleteOrder,
  ParamsMatchOrder,
  ParamsReceptOrder,
  ParamsUpdateOrder,
  receptOrder,
  unmatchOrder,
  updateOrder,
} from '../apis/order.api';
import {
  defaultGetNextPageParam,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

const invalidationKeys = [
  queryKey.orders_interval,
  queryKey.orders,
  queryKey.study_detail,
  queryKey.orderWorklist_interval,
  queryKey.orderWorklist,
];

export function useGetDeviceStatistics(
  params: Parameters<typeof getDeviceStatistics>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.deviceStatistics, sortObjectKeys(params)],
    () => getDeviceStatistics(params),
    {
      ...options,
      onError: (err) => {
        console.error('getDeviceStatistics:', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getDeviceStatistics>>>;
}

export function useGetDeviceStatisticsAll(
  params: Parameters<typeof getDeviceStatisticsAll>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.deviceStatisticsAll, sortObjectKeys(params)],
    () => getDeviceStatisticsAll(params),
    {
      ...options,
      onError: (err) => {
        console.error('getDeviceStatisticsAll:', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getDeviceStatistics>>>;
}

export const useGetDeviceStatisticsInterval = (
  params: Parameters<typeof getDeviceStatistics>[0],
  options?: UseQueryOptions
) => {
  return useQuery(
    [queryKey.deviceStatistics_interval, sortObjectKeys(params)],
    () => getDeviceStatistics(params),
    {
      refetchInterval: 10_000,
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getDeviceStatistics>>>;
};

export function useGetOrders(
  params: Parameters<typeof getOrders>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.orders, sortObjectKeys(params)],
    () => getOrders(params),
    {
      ...options,
      onError: (err) => {
        console.error('useGetOrders:', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getOrders>>>;
}

export const useGetOrdersInterval = (
  params: Parameters<typeof getOrders>[0],
  options?: UseQueryOptions
) => {
  return useQuery(
    [queryKey.orders_interval, sortObjectKeys(params)],
    () => getOrders(params),
    {
      refetchInterval: 10_000,
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getOrders>>>;
};

export function useGetOrderWorklist(
  params: Parameters<typeof getOrderWorklist>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.orderWorklist, sortObjectKeys(params)],
    () => getOrderWorklist(params),
    {
      ...options,
      onError: (err) => {
        console.error('useGetOrders:', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getOrderWorklist>>>;
}

export const useGetOrderWorklistInterval = (
  params: Parameters<typeof getOrderWorklist>[0],
  options?: UseQueryOptions
) => {
  return useQuery(
    [queryKey.orderWorklist_interval, sortObjectKeys(params)],
    () => getOrderWorklist(params),
    {
      getNextPageParam: defaultGetNextPageParam,
      refetchInterval: 10_000,
      keepPreviousData: true,
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getOrderWorklist>>>;
};

export const useInfinityWorkList = (
  params: Parameters<typeof getOrderWorklist>[0],
  options?: UseInfiniteQueryOptions
) => {
  const queryInfo = useInfiniteQuery(
    [queryKey.orderWorklist, sortObjectKeys(params)],
    (e) =>
      getOrderWorklist({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      keepPreviousData: true,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getOrderWorklist>>>;

  const data = queryInfo.data?.pages.map((page) => page.data || []).flat();

  return {
    ...queryInfo,
    data,
    count: queryInfo.data?.pages[0].count,
  };
};

export function useGetOrderDetail(id, options?: UseQueryOptions) {
  return useQuery([queryKey.orderDetail, id], () => getOrderDetail(id), {
    ...options,
    onSuccess: (data: Awaited<ReturnType<typeof getOrderDetail>>) => {
      options?.onSuccess?.(data.data);
    },
  }) as UseQueryResult<Awaited<ReturnType<typeof getOrderDetail>>>;
}

export function useMutationGetOrder(
  options?: Parameters<typeof useMutation>['2']
) {
  return useMutation<
    Awaited<ReturnType<typeof getOrderDetail>>,
    any,
    Parameters<typeof getOrderDetail>['0']
  >(async (params) => getOrderDetail(params), {
    onError: (err) => {
      console.error('useMutationGetOrderDetail:', err);
    },
    ...options,
  });
}

export function useCreateOrderGroup() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateOrderGroup) => createOrderGroup(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.orders_interval]);
        queryClient.invalidateQueries([queryKey.orders]);
      },
      onError: (err) => {
        console.error('useCreateOrderGroup:', err);
      },
    }
  );
}

export function useUpdateOrder() {
  const queryClient = useQueryClient();

  return useMutation(async (params: ParamsUpdateOrder) => updateOrder(params), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.orders_interval]);
      queryClient.invalidateQueries([queryKey.orderWorklist_interval]);
      queryClient.invalidateQueries([queryKey.orderWorklist]);
      queryClient.invalidateQueries([queryKey.orderDetail]);
    },
    onError: (err) => {
      console.error('useUpdateOrder:', err);
    },
  });
}

export function useDeleteOrder() {
  const queryClient = useQueryClient();

  return useMutation(async (params: ParamsDeleteOrder) => deleteOrder(params), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.orders_interval]);
    },
    onError: (err) => {
      console.error('useDeleteOrder:', err);
    },
  });
}

export function useMatchOrder() {
  const queryClient = useQueryClient();

  return useMutation((params: ParamsMatchOrder) => matchOrder(params), {
    onSuccess: async () => {
      for (const key of invalidationKeys) {
        await queryClient.invalidateQueries([key]);
      }
    },
    onError: (err) => {
      console.error('useMatchOrder:', err);
    },
  });
}

export function useUnmatchOrder() {
  const queryClient = useQueryClient();

  return useMutation((params: ParamsMatchOrder) => unmatchOrder(params), {
    onSuccess: async () => {
      for (const key of invalidationKeys) {
        await queryClient.invalidateQueries([key]);
      }
    },
    onError: (err) => {
      console.error('useUnmatchOrder:', err);
    },
  });
}

export function useReceptOrder() {
  const queryClient = useQueryClient();

  return useMutation((params: ParamsReceptOrder) => receptOrder(params), {
    onSuccess: async () => {
      for (const key of invalidationKeys) {
        await queryClient.invalidateQueries([key]);
      }
    },
    onError: (err) => {
      console.error('useReceptOrder:', err);
    },
  });
}

export function useChangeOrderDevice() {
  const queryClient = useQueryClient();

  return useMutation(
    (params: ParamsChangeOrderDevice) => changeOrderDevice(params),
    {
      onSuccess: async () => {
        for (const key of invalidationKeys) {
          await queryClient.invalidateQueries([key]);
        }
      },
      onError: (err) => {
        console.error('useReceptOrder:', err);
      },
    }
  );
}
